import React, { useRef, useEffect, useMemo } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useShallow } from "zustand/shallow";
import { loadModelPhased } from "../../lib/loaders/loadModelPhased";
import { yieldToMain } from "../../lib/yieldToMain";
import gsap from "gsap";
import { createMetricLScaleOverlay } from "../../lib/MetricLScaleOverlay";
import { scaleModelToCentimeters } from "../../lib/ModelScaler";
import { isLockingSocketScaleDebugEnabled, trackMeshScale } from "../../lib/lockingSocketScaleDebug";
import {
  enforceOrbitEnabled,
  installOrbitPointerTracking,
  parkOrbit,
  releaseOrbit,
} from "../../lib/orbitControlsGuard";
import { SixAxisHelper } from "../../lib/SixAxisHelper";
import { Plus, Settings, X } from "lucide-react";

/** Orbit-park owner id for the programmatic view-preset camera tween. */
const ORBIT_PARK_CAMERA_PRESET = 'camera-preset-tween';
import useEditorStore from "../../stores/useEditorStore";
import { ToolHandler } from "./ToolHandler";
import WorkerLoadingIndicator from "../layout/WorkerLoadingIndicator";
import StepSceneControls from "./StepSceneControls";
import MeasurementToolButtons from "./MeasurementToolButtons";
import { WORKFLOW_TYPES, isFinalizationPath, isPreviewStepPath } from "../../constants/flowConfig";
import useWorkflowStore from "../../stores/useWorkflowStore";
import { useLocation } from "react-router-dom";
import { computeBoundsTree } from "three-mesh-bvh";
import { cn } from "../../lib/utils.js";
import { styleCompareOverlayMaterial } from "../../constants/materialConfig";
import {
  captureCompareBaseline,
  hasCompareBaseline,
  instantiateCompareBaselineMesh,
} from "../../lib/compareBaselineSnapshot";
import { resolveSceneModelSource, sceneHasLoadedMesh } from "../../lib/scanMeshLoadGate";
import { disposeObject3D as disposeObjectMaterialsAndGeometry } from "../../lib/threeDispose";

const ghostModeIcon = "/other_icons/ghost_full.svg";
const colorModeIcon = "/other_icons/contrast.svg";
const scaleOverlayIcon = "/tool_icons/length.svg";
const compareModeIcon = "/other_icons/compare.svg";
const ANATOMICAL_VIEWS = [
  { id: 'front', name: 'Left side view', distAxis: 'z', distSign: 1, up: [0, 1, 0] },
  { id: 'back', name: 'Right side view', distAxis: 'z', distSign: -1, up: [0, 1, 0] },
  { id: 'top', name: 'Top view', distAxis: 'y', distSign: 1, up: [0, 0, -1] },
  { id: 'bottom', name: 'Bottom view', distAxis: 'y', distSign: -1, up: [0, 0, 1] },
  { id: 'left', name: 'Back view', distAxis: 'x', distSign: -1, up: [0, 1, 0] },
  { id: 'right', name: 'Front view', distAxis: 'x', distSign: 1, up: [0, 1, 0] },
];
const VIEW_SHORT_LABELS = {
  front: 'L',
  back: 'R',
  top: 'T',
  bottom: 'Bo',
  left: 'B',
  right: 'F',
};

function ScaleHoverReadout() {
  const scaleOverlayVisible = useEditorStore((s) => s.scaleOverlayVisible);
  const readout = useEditorStore((s) => s.scaleHoverReadout);
  if (!scaleOverlayVisible) return null;
  return (
    <div
      className="pointer-events-none absolute bottom-3 left-3 z-30 max-w-sm rounded-lg border border-white/50 bg-white/85 px-3 py-2 text-xs font-medium text-slate-800 shadow-md backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      {readout ? (
        <div className="flex flex-col gap-0.5">
          <span>
            Width from corner:{" "}
            <span className="font-semibold tabular-nums text-red-600">{readout.widthCm.toFixed(2)}</span>{" "}
            cm
          </span>
          <span>
            Height from corner:{" "}
            <span className="font-semibold tabular-nums text-red-600">{readout.heightCm.toFixed(2)}</span>{" "}
            cm
          </span>
          <span>
            Depth from corner:{" "}
            <span className="font-semibold tabular-nums text-red-600">{readout.depthCm.toFixed(2)}</span>{" "}
            cm
          </span>
        </div>
      ) : (
        <span className="text-slate-500">Hover model for distances</span>
      )}
    </div>
  );
}

export const ThreeScene = ({ children }) => {
  const location = useLocation();
  const isFinalPreviewStep = isPreviewStepPath(location.pathname);
  const isFinalizationStep = isFinalizationPath(location.pathname);
  const mountRef = useRef(null);
  const scanPreparingSixAxisRef = useRef(null);
  const metricScaleOverlayApiRef = useRef(null);
  /** Small sphere on mesh at scale-hover raycast hit (world space). */
  const metricScaleHoverMarkerRef = useRef(null);
  /** Latest pointer for scale hover raycast (updated in rAF). */
  const pointerScreenRef = useRef({ active: false, cx: 0, cy: 0 });
  const mainViewportRef = useRef(null);
  const secondaryViewsRef = useRef(null);
  const secondaryCellRefs = useRef({});
  const secondaryCanvasWrapRefs = useRef({});
  const didInitDefaultViewsRef = useRef(false);
  const loadedModelKeyRef = useRef(null);
  const viewToggles = useEditorStore((state) => state.viewToggles);
  const setViewToggle = useEditorStore((state) => state.setViewToggle);
  const rightPanelVisible = useEditorStore((state) => state.rightPanelVisible);
  const setRightPanelVisible = useEditorStore((state) => state.setRightPanelVisible);
  const activeSecondaryViewCount = useMemo(
    () => Object.values(viewToggles || {}).filter(Boolean).length,
    [viewToggles]
  );
  const shouldShowSecondaryViews =
    !isFinalPreviewStep && !isFinalizationStep && activeSecondaryViewCount > 0;
  const canAddSecondaryView =
    !isFinalPreviewStep && !isFinalizationStep && activeSecondaryViewCount < 2;
  const visibleSecondaryViewIds = useMemo(
    () => ANATOMICAL_VIEWS.filter((v) => !!viewToggles?.[v.id]).map((v) => v.id),
    [viewToggles]
  );

  // Get state and setters from the Zustand store
  const setSceneRefs = useEditorStore((state) => state.setSceneRefs);
  const sceneRefs = useEditorStore((state) => state.sceneRefs);
  const cameraPosition = useEditorStore((state) => state.cameraPosition);
  const setIsLoading = useEditorStore((state) => state.setIsLoading);
  const uploadedModelData = useEditorStore((state) => state.uploadedModelData);
  const modelScaleUnit = useEditorStore((state) => state.modelScaleUnit);
  const modelManualScale = useEditorStore((state) => state.modelManualScale);
  const setModelScaleInfo = useEditorStore((state) => state.setModelScaleInfo);
  const setMeshStats = useEditorStore((state) => state.setMeshStats);

  const [
    isLoadingPatientData,
    isFetchingScanFiles,
    scanFilesMeta,
    scanAutoLoaded,
    patientData,
    patientDataError,
    bkCheckpointHydrating,
  ] = useWorkflowStore(
    useShallow((s) => [
      s.isLoadingPatientData,
      s.isFetchingScanFiles,
      s.scanFilesMeta,
      s.scanAutoLoaded,
      s.patientData,
      s.patientDataError,
      s.bkCheckpointHydrating,
    ])
  );

  const [
    ghostModeEnabled,
    colorModeEnabled,
    toggleGhostMode,
    toggleColorMode,
    showUnmodifiedMesh,
    toggleUnmodifiedMesh,
    scanPreparingSixAxisVisible,
    toggleScanPreparingSixAxis,
    scaleOverlayVisible,
    toggleScaleOverlay,
  ] = useEditorStore(
    useShallow((state) => [
      state.ghostModeEnabled,
      state.colorModeEnabled,
      state.toggleGhostMode,
      state.toggleColorMode,
      state.showUnmodifiedMesh,
      state.toggleUnmodifiedMesh,
      state.scanPreparingSixAxisVisible,
      state.toggleScanPreparingSixAxis,
      state.scaleOverlayVisible,
      state.toggleScaleOverlay,
    ])
  );

  useEffect(() => {
    const um = sceneRefs?.unmodifiedMesh;
    if (!um) return;
    um.visible = showUnmodifiedMesh;
    styleCompareOverlayMaterial(um.material, showUnmodifiedMesh);
  }, [sceneRefs?.unmodifiedMesh, showUnmodifiedMesh]);

  // Effect to set up the scene (runs only once)
  useEffect(() => {
    console.log(`setting up threejs scene`);
    // This effect sets up the entire Three.js scene once
    const width = mainViewportRef.current.clientWidth;
    const height = mainViewportRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xb8bcc4);

    // Orthographic camera for consistent viewing without perspective distortion
    const aspect = width / height;
    const viewSize = 35; // Half-height in world units - adjust based on model size
    const camera = new THREE.OrthographicCamera(
      -viewSize * aspect,  // left
      viewSize * aspect,   // right
      viewSize,            // top
      -viewSize,           // bottom
      0.001,                 // near
      200000               // far (large helpers must stay inside frustum)
    );
    camera.position.set(0, 0, 30);

    const renderer = new THREE.WebGLRenderer({ antialias: true, outputColorSpace: THREE.SRGBColorSpace });
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.setSize(width, height);
    mainViewportRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.rotateSpeed = 0.25;
    controls.zoomSpeed = 0.35;
    controls.panSpeed = 0.35;

    // Left = orbit, Middle = dolly, Right = pan (OrbitControls default for RIGHT is PAN)
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN
    };

    const preventContextMenu = (e) => {
      e.preventDefault();
    };

    renderer.domElement.addEventListener('contextmenu', preventContextMenu);

    // Feeds the orbit guard's "is a drag still in flight" test, which is what
    // lets it tell a tool that is mid-stroke from one that leaked its restore.
    const uninstallOrbitPointerTracking = installOrbitPointerTracking();

    //Create origin sphere
    // const originSphere = new THREE.Mesh(
    //   new THREE.SphereGeometry(0.25, 32, 32),
    //   new THREE.MeshBasicMaterial({ color: 0xaaaaaa, depthTest: false, depthWrite: false, transparent: true, opacity: 0.5 })
    // );
    // originSphere.position.set(0, 0, 0);
    // scene.add(originSphere);

    // Add lights
    scene.add(new THREE.AmbientLight(0xffffff, 2));

    const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    const directionLight2 = new THREE.DirectionalLight(0xffffff, 3);
    directionLight2.position.set(-5, -5, -5);
    scene.add(directionLight2);

    // Set refs only in the Zustand store (scene ready but no model yet)
    setSceneRefs({ scene, camera, renderer, controls, shadedMesh: null, material: null, unmodifiedMesh: null, originalCamera: camera });

    // Handle window resize - update orthographic frustum
    const handleResize = () => {
      if (mountRef.current) {
        const newWidth = mainViewportRef.current.clientWidth;
        const newHeight = mainViewportRef.current.clientHeight;
        const newAspect = newWidth / newHeight;

        // Update orthographic camera frustum to maintain consistent view
        camera.left = -viewSize * newAspect;
        camera.right = viewSize * newAspect;
        camera.top = viewSize;
        camera.bottom = -viewSize;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);

        // Update debug edge visualization resolution if enabled
        if (import.meta.env.VITE_DEBUG_THREE === 'true') {
          import('../../lib/debugMeshEdges.js').then(({ updateEdgeVisualizationResolution }) => {
            updateEdgeVisualizationResolution(scene, renderer)
          })
        }
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mainViewportRef.current);

    // --- 6 anatomical orthographic views ---
    const ORTHO_VIEW_SIZE_INITIAL = 15;
    const ORTHO_VIEW_SIZE_MIN = 2;
    const ORTHO_VIEW_SIZE_MAX = 80;
    const ORTHO_DIST = 100;
    const focusPoint = new THREE.Vector3(0, 0, 0); // bottom of model when available
    let orthoViewSizeShared = ORTHO_VIEW_SIZE_INITIAL;

    function getFocusPoint(out) {
      const refs = useEditorStore.getState().sceneRefs;
      const { currentWorkflow } = useWorkflowStore.getState();
      if (refs?.shadedMesh) {
        const box = new THREE.Box3().setFromObject(refs.shadedMesh);
        box.getCenter(out);
        if (currentWorkflow !== WORKFLOW_TYPES.SCAN_PROCESS) {
          out.y = box.min.y; // bottom of model
        }
        return out;
      }
      out.set(0, 0, 0);
      return out;
    }

    const multiViewViews = [];

    ANATOMICAL_VIEWS.forEach((cfg) => {
      const cell = secondaryCellRefs.current[cfg.id];
      const canvasWrap = secondaryCanvasWrapRefs.current[cfg.id];
      if (!cell || !canvasWrap) return;

      const orthoCamera = new THREE.OrthographicCamera(
        -ORTHO_VIEW_SIZE_INITIAL, ORTHO_VIEW_SIZE_INITIAL,
        ORTHO_VIEW_SIZE_INITIAL, -ORTHO_VIEW_SIZE_INITIAL, 0.001, 200000
      );
      const pos = new THREE.Vector3(0, 0, 0);
      if (cfg.distAxis === 'x') pos.x = cfg.distSign * ORTHO_DIST;
      else if (cfg.distAxis === 'y') pos.y = cfg.distSign * ORTHO_DIST;
      else pos.z = cfg.distSign * ORTHO_DIST;
      orthoCamera.position.copy(pos);
      orthoCamera.up.set(cfg.up[0], cfg.up[1], cfg.up[2]);
      orthoCamera.lookAt(0, 0, 0);

      const viewRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      viewRenderer.setSize(1, 1);
      viewRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      viewRenderer.setClearColor(0x2a2a2a);
      viewRenderer.outputColorSpace = THREE.SRGBColorSpace;
      canvasWrap.innerHTML = '';
      canvasWrap.appendChild(viewRenderer.domElement);
      viewRenderer.domElement.style.display = 'block';

      viewRenderer.domElement.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 1.04 : 1 / 1.04;
        orthoViewSizeShared = Math.max(ORTHO_VIEW_SIZE_MIN, Math.min(ORTHO_VIEW_SIZE_MAX, orthoViewSizeShared * delta));
      }, { passive: false });

      multiViewViews.push({
        id: cfg.id,
        cell,
        camera: orthoCamera,
        renderer: viewRenderer,
        ...cfg,
      });
    });

    function updateMultiViewCameras() {
      getFocusPoint(focusPoint);
      const size = orthoViewSizeShared;
      multiViewViews.forEach((v) => {
        const cam = v.camera;
        const rect = v.cell.getBoundingClientRect();
        const aspect = rect.height > 0 ? rect.width / rect.height : 1;
        const pos = focusPoint.clone();
        if (v.distAxis === 'x') pos.x += v.distSign * ORTHO_DIST;
        else if (v.distAxis === 'y') pos.y += v.distSign * ORTHO_DIST;
        else pos.z += v.distSign * ORTHO_DIST;
        cam.position.copy(pos);
        cam.lookAt(focusPoint);
        cam.up.set(v.up[0], v.up[1], v.up[2]);
        cam.left = -size * aspect;
        cam.right = size * aspect;
        cam.top = size;
        cam.bottom = -size;
        cam.updateProjectionMatrix();
      });
    }

    function renderMultiViews() {
      const toggles = useEditorStore.getState().viewToggles || {};
      updateMultiViewCameras();

      multiViewViews.forEach((v) => {
        const visible = !!toggles[v.id];
        if (visible) {
          const rect = v.cell.getBoundingClientRect();
          const nextW = Math.max(1, Math.floor(rect.width));
          const nextH = Math.max(1, Math.floor(rect.height));
          const currentW = v.renderer.domElement.clientWidth;
          const currentH = v.renderer.domElement.clientHeight;
          if (currentW !== nextW || currentH !== nextH) {
            v.renderer.setSize(nextW, nextH, false);
            const aspect = nextW / nextH;
            const size = orthoViewSizeShared;
            v.camera.left = -size * aspect;
            v.camera.right = size * aspect;
            v.camera.top = size;
            v.camera.bottom = -size;
            v.camera.updateProjectionMatrix();
          }
          v.renderer.render(scene, v.camera);
        }
      });
    }

    const raycaster = new THREE.Raycaster();
    const scaleHoverNormalScratch = new THREE.Vector3();

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      const { camera: cam, controls: ctrl, shadedMesh, renderer: mainRenderer } =
        useEditorStore.getState().sceneRefs;
      if (!cam || !ctrl || !mainRenderer) return;
      // Orbit stays disabled only while something is actually holding it. This is
      // the one place that runs on every route, every step and every tool, so it
      // is where the invariant is repaired when a tool is torn down mid-drag and
      // never restores it. See lib/orbitControlsGuard.
      enforceOrbitEnabled(ctrl);
      ctrl.update();

      const st = useEditorStore.getState();
      const compareMesh = st.sceneRefs?.unmodifiedMesh;
      // Keep compare overlay TRS matched to the shaded mesh whenever both exist (locking attach/undo, toggling compare).
      if (shadedMesh && compareMesh) {
        compareMesh.position.copy(shadedMesh.position);
        compareMesh.quaternion.copy(shadedMesh.quaternion);
        compareMesh.scale.copy(shadedMesh.scale);
      }

      if (isLockingSocketScaleDebugEnabled() && shadedMesh) {
        trackMeshScale(shadedMesh, 'ThreeScene:animate', {
          flexDeg: st.alignmentSocketRedRingXDeg,
          abdDeg: st.alignmentSocketAbductionZDeg,
        });
      }

      const scaleOverlay = metricScaleOverlayApiRef.current;
      const ptr = pointerScreenRef.current;

      if (scaleOverlay) {
        scaleOverlay.syncToMesh(shadedMesh);
      }

      if (
        st.scaleOverlayVisible &&
        scaleOverlay &&
        ptr.active &&
        shadedMesh &&
        mainRenderer.domElement
      ) {
        const rect = mainRenderer.domElement.getBoundingClientRect();
        if (
          ptr.cx >= rect.left &&
          ptr.cx <= rect.right &&
          ptr.cy >= rect.top &&
          ptr.cy <= rect.bottom
        ) {
          const ndc = new THREE.Vector2(
            ((ptr.cx - rect.left) / rect.width) * 2 - 1,
            -((ptr.cy - rect.top) / rect.height) * 2 + 1
          );
          raycaster.setFromCamera(ndc, cam);
          const geom = shadedMesh.geometry;
          if (geom && !geom.boundsTree) {
            computeBoundsTree.call(geom);
          }
          const hits = raycaster.intersectObject(shadedMesh, false);
          const hoverMarker = metricScaleHoverMarkerRef.current;
          if (hits.length > 0) {
            const hit = hits[0];
            const p = hit.point;
            const g = scaleOverlay.group.position;
            const lx = p.x - g.x;
            const ly = p.y - g.y;
            const lz = p.z - g.z;
            scaleOverlay.setLabelHighlight(lx, ly, lz);
            // Scene units are cm; overlay axes = width (X) / height (Y) / depth (Z) from corner
            st.setScaleHoverReadout({
              widthCm: lx,
              heightCm: ly,
              depthCm: lz,
            });
            if (hoverMarker) {
              hoverMarker.position.copy(p);
              if (hit.face) {
                scaleHoverNormalScratch
                  .copy(hit.face.normal)
                  .transformDirection(hit.object.matrixWorld)
                  .normalize();
                hoverMarker.position.addScaledVector(scaleHoverNormalScratch, 0.06);
              }
              hoverMarker.visible = true;
            }
          } else {
            scaleOverlay.clearLabelHighlight();
            st.setScaleHoverReadout(null);
            if (hoverMarker) hoverMarker.visible = false;
          }
        } else {
          scaleOverlay.clearLabelHighlight();
          st.setScaleHoverReadout(null);
          if (metricScaleHoverMarkerRef.current) {
            metricScaleHoverMarkerRef.current.visible = false;
          }
        }
      } else {
        if (metricScaleHoverMarkerRef.current) {
          metricScaleHoverMarkerRef.current.visible = false;
        }
        if (!st.scaleOverlayVisible && st.scaleHoverReadout != null) {
          st.setScaleHoverReadout(null);
        }
      }

      if (scaleOverlay) {
        scaleOverlay.update(cam);
      }
      mainRenderer.render(scene, cam);
      renderMultiViews();
    };
    animate();

    return () => {
      // Clean up listeners
      renderer.domElement.removeEventListener('contextmenu', preventContextMenu);

      if (mainViewportRef.current && renderer.domElement) {
        mainViewportRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();

      multiViewViews.forEach((v) => {
        v.renderer.dispose();
        if (v.renderer.domElement?.parentNode) v.renderer.domElement.parentNode.removeChild(v.renderer.domElement);
      });
      secondaryCanvasWrapRefs.current = {};
      secondaryCellRefs.current = {};

      if (mainViewportRef.current) resizeObserver.unobserve(mainViewportRef.current);
      resizeObserver.disconnect();
      uninstallOrbitPointerTracking();

      console.log(`cleaning up threejs scene`);
    };
  }, [setSceneRefs]); // Only run once on mount

  // Scan-fetch overlay only — must not re-run model load on step navigation.
  useEffect(() => {
    const sceneRefsState = useEditorStore.getState().sceneRefs;
    if (sceneHasLoadedMesh(sceneRefsState) || uploadedModelData) return;

    // During checkpoint hydration the resume loader owns the overlay + progress
    // (it drives a real download progress bar). Don't set/reset it here, or the
    // bar snaps back to 0 on every scan-files state change.
    if (bkCheckpointHydrating) return;

    const { awaiting } = resolveSceneModelSource({
      uploadedModelData,
      isLoadingPatientData,
      isFetchingScanFiles,
      scanFilesMeta,
      scanAutoLoaded,
      patientData,
      patientDataError,
      pathname: location.pathname,
      bkCheckpointHydrating,
    });

    if (!awaiting) return;

    setIsLoading(true, 'Loading scan…');
    return () => {
      // Never tear the overlay down while a checkpoint mesh is still hydrating
      // (its download owns the overlay); read fresh state to avoid a stale flag.
      if (!useWorkflowStore.getState().bkCheckpointHydrating) setIsLoading(false);
    };
  }, [
    uploadedModelData,
    isLoadingPatientData,
    isFetchingScanFiles,
    scanFilesMeta,
    scanAutoLoaded,
    patientData,
    patientDataError,
    location.pathname,
    setIsLoading,
    bkCheckpointHydrating,
  ]);

  // Model load — keyed on source/scale only (not route).
  useEffect(() => {
    const sceneRefsState = useEditorStore.getState().sceneRefs;
    if (!sceneRefsState || !sceneRefsState.scene) return;

    const { modelSource, awaiting } = resolveSceneModelSource({
      uploadedModelData,
      isLoadingPatientData,
      isFetchingScanFiles,
      scanFilesMeta,
      scanAutoLoaded,
      patientData,
      patientDataError,
      pathname: location.pathname,
      bkCheckpointHydrating,
    });

    if (awaiting || !modelSource) {
      if (!awaiting && !modelSource) setIsLoading(false);
      return;
    }

    const modelSourceKey =
      typeof modelSource === 'string'
        ? modelSource
        : `${modelSource?.type ?? ''}:${modelSource?.source ?? ''}:${modelSource?.data ?? ''}`;
    const loadKey = `${modelSourceKey}|${modelScaleUnit}|${modelManualScale}`;
    if (sceneHasLoadedMesh(sceneRefsState) && loadedModelKeyRef.current === loadKey) {
      return;
    }
    loadedModelKeyRef.current = loadKey;

    console.log('Loading model from source:', modelSource);

    const abortController = new AbortController();
    // A checkpoint mesh was already downloaded by the resume loader, which drove
    // the bar up to ~80%. Continue from there (upper band) instead of restarting
    // at 10, so the bar never jumps backwards.
    const isCheckpointLoad =
      typeof modelSource === 'object' && modelSource?.source === 'checkpoint';
    // Map each load phase to a progress percentage so the overlay shows a
    // single "Loading" label with a filling progress bar instead of phase text.
    const PHASE_PROGRESS = isCheckpointLoad
      ? {
          'Loading file…': 82,
          'Centering model…': 85,
          'Merging vertices…': 88,
          'Loading…': 90,
          'Finalizing mesh…': 93,
          'Scaling model…': 96,
          'Preparing comparison view…': 98,
        }
      : {
          'Loading file…': 10,
          'Centering model…': 20,
          'Merging vertices…': 40,
          'Loading…': 60, // topology repair
          'Finalizing mesh…': 75,
          'Scaling model…': 85,
          'Preparing comparison view…': 92,
        };
    const setLoadingProgress = (p) => useEditorStore.getState().setLoadingProgress(p);
    const setLoadingMessage = (msg) => {
      const next = PHASE_PROGRESS[msg];
      if (typeof next === 'number') setLoadingProgress(next);
    };
    // Resolve after the renderer has painted at least one frame with the new
    // mesh, so the overlay only disappears once the model is actually visible.
    const waitForRender = () =>
      new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

    // For a checkpoint, keep the download's progress (setLoadingActive doesn't
    // reset to 0); for a fresh load, start the bar from the beginning.
    if (isCheckpointLoad) {
      useEditorStore.getState().setLoadingActive('Loading file…');
    } else {
      setIsLoading(true, 'Loading file…');
    }

    if (sceneRefsState.shadedMesh) {
      sceneRefsState.scene.remove(sceneRefsState.shadedMesh);
      sceneRefsState.shadedMesh.geometry?.dispose();
      sceneRefsState.shadedMesh.material?.dispose();
    }
    if (sceneRefsState.unmodifiedMesh) {
      sceneRefsState.scene.remove(sceneRefsState.unmodifiedMesh);
      if (sceneRefsState.unmodifiedMesh.geometry !== sceneRefsState.shadedMesh?.geometry) {
        sceneRefsState.unmodifiedMesh.geometry?.dispose();
      }
      sceneRefsState.unmodifiedMesh.material?.dispose();
    }

    const runPhasedLoad = async () => {
      try {
        const mesh = await loadModelPhased(modelSource, {
          onPhase: setLoadingMessage,
          yieldToMain,
        });
        if (abortController.signal.aborted) return;

        setMeshStats(mesh.userData?.meshStats ?? null);

        console.log('[ThreeScene] Step: Scaling model (main thread)');
        setLoadingMessage('Scaling model…');
        await yieldToMain();
        if (abortController.signal.aborted) return;

        const preferredUnit =
          uploadedModelData && typeof uploadedModelData === 'object'
            ? (uploadedModelData.preferredScaleUnit ?? modelScaleUnit)
            : modelScaleUnit;
        const scaleInfo = scaleModelToCentimeters(mesh, preferredUnit, modelManualScale);
        setModelScaleInfo(scaleInfo);
        console.log('✅ Model scaled and centered. Dimensions (cm):', scaleInfo.dimensions);

        console.log('[ThreeScene] Step: Preparing comparison view (main thread)');
        setLoadingMessage('Preparing comparison view…');
        await yieldToMain();
        if (abortController.signal.aborted) return;

        sceneRefsState.scene.add(mesh);

        const overlayMaterial = new THREE.MeshMatcapMaterial({
          vertexColors: false,
          color: '#a8d8f0',
          transparent: true,
          opacity: 0.5,
          side: THREE.DoubleSide,
          depthTest: false,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -0.1,
        });

        // A checkpoint mesh is the socket *after* earlier steps — cloning it would
        // make Compare show an already-cut "original". Rebuild the overlay from the
        // load-time snapshot instead (it carries any alignment/reframe baked since).
        // Only a genuine model load establishes a new baseline.
        let unmodifiedMesh = null;
        if (isCheckpointLoad && hasCompareBaseline()) {
          unmodifiedMesh = instantiateCompareBaselineMesh(overlayMaterial);
        }
        if (unmodifiedMesh) {
          unmodifiedMesh.userData.compareBaselineSource = 'snapshot';
        } else {
          if (!isCheckpointLoad) captureCompareBaseline(mesh);
          unmodifiedMesh = mesh.clone();
          unmodifiedMesh.geometry = mesh.geometry.clone();
          unmodifiedMesh.material = overlayMaterial;
          // A fresh load IS the original, so cloning it gives a real baseline. A
          // checkpoint load with no surviving snapshot does not: that mesh is the
          // socket after earlier steps, and anything that MEASURES the overlay
          // (Circumference tools' Scan column) has to know it is looking at a
          // stand-in rather than an original, or it reports the modified socket
          // in both columns.
          unmodifiedMesh.userData.compareBaselineSource = isCheckpointLoad
            ? 'currentMesh'
            : 'snapshot';
        }
        sceneRefsState.scene.add(unmodifiedMesh);
        unmodifiedMesh.visible = useEditorStore.getState().showUnmodifiedMesh;
        unmodifiedMesh.renderOrder = 5e5;
        styleCompareOverlayMaterial(unmodifiedMesh.material, unmodifiedMesh.visible);

        const normalMaterial = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });

        if (import.meta.env.VITE_DEBUG_THREE === 'true') {
          import('../../lib/debugMeshEdges.js').then(({ createEdgeVisualization }) => {
            createEdgeVisualization(mesh.geometry, sceneRefsState.scene, {
              showManifold: false,
              showBoundary: true,
              showNonManifold: true,
              lineWidth: 10,
              depthTest: false,
            }, sceneRefsState.renderer);
          });
        }

        setSceneRefs({ ...sceneRefsState, shadedMesh: mesh, material: mesh.material, unmodifiedMesh, normalMaterial });

        // Orbit target on world X-axis at model lateral center (y=z=0). After centerModel, center.x is 0 → origin.
        const refs = useEditorStore.getState().sceneRefs;
        if (refs?.controls && refs?.shadedMesh) {
          const box = new THREE.Box3().setFromObject(refs.shadedMesh);
          const c = new THREE.Vector3();
          box.getCenter(c);
          refs.controls.target.set(c.x, 0, 0);
        }

        // Keep the overlay up until the mesh has actually been painted so the
        // user never sees an empty scene after the loader disappears.
        await waitForRender();
        if (abortController.signal.aborted) return;
        setLoadingProgress(100);
        await waitForRender();
      } catch (err) {
        console.error('Error loading model:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => runPhasedLoad(), { timeout: 2000 });
      } else {
        setTimeout(runPhasedLoad, 0);
      }
    };
    schedule();

    return () => {
      abortController.abort();
    };
  }, [
    uploadedModelData,
    modelScaleUnit,
    modelManualScale,
    setSceneRefs,
    setModelScaleInfo,
    setMeshStats,
    setIsLoading,
    scanAutoLoaded,
    scanFilesMeta,
    patientData,
    patientDataError,
    isLoadingPatientData,
    isFetchingScanFiles,
    bkCheckpointHydrating,
  ]);

  // Programmatic view presets: animate position while OrbitControls is paused so damping
  // does not fight GSAP (was causing intermittent jitter / lag).
  useEffect(() => {
    if (!sceneRefs?.camera || cameraPosition == null) return;
    const { camera, controls } = sceneRefs;
    const dest = cameraPosition;
    if (typeof dest.x !== 'number' || typeof dest.y !== 'number' || typeof dest.z !== 'number') return;

    const dx = camera.position.x - dest.x;
    const dy = camera.position.y - dest.y;
    const dz = camera.position.z - dest.z;
    if (dx * dx + dy * dy + dz * dz < 1e-6) {
      camera.lookAt(0, 0, 0);
      if (controls) {
        controls.target.set(0, 0, 0);
        controls.update();
      }
      return;
    }

    gsap.killTweensOf(camera.position);

    // A registered park, not a raw `enabled = false`: this is the one place that
    // parks orbit without a pointer being down, so the guard has to be able to
    // see the hold or it would heal the tween out from under itself.
    //
    // Snapshotting the previous `enabled` and restoring it is also what let a
    // transient disable get latched permanently — if the flag happened to be
    // false when the preset fired, it was restored to false forever. The park
    // registry has no such state to latch: releasing simply un-holds.
    const prevDamping = controls?.enableDamping ?? true;
    if (controls) {
      parkOrbit(ORBIT_PARK_CAMERA_PRESET, controls);
      controls.enableDamping = false;
      controls.target.set(0, 0, 0);
      controls.update();
    }

    const tween = gsap.to(camera.position, {
      x: dest.x,
      y: dest.y,
      z: dest.z,
      duration: 0.45,
      ease: 'power2.inOut',
      overwrite: 'auto',
      onUpdate: () => {
        camera.lookAt(0, 0, 0);
      },
      onComplete: () => {
        camera.lookAt(0, 0, 0);
        if (controls) {
          controls.target.set(0, 0, 0);
          controls.enableDamping = prevDamping;
          releaseOrbit(ORBIT_PARK_CAMERA_PRESET, controls);
          controls.update();
        }
      },
    });

    return () => {
      tween.kill();
      if (controls) {
        controls.enableDamping = prevDamping;
        releaseOrbit(ORBIT_PARK_CAMERA_PRESET, controls);
        controls.update();
      }
    };
  }, [cameraPosition, sceneRefs?.camera, sceneRefs?.controls]);

  // Optional six-axis arrows (+/− X/Y/Z) on any route that mounts this scene
  useEffect(() => {
    const scene = sceneRefs?.scene;
    if (!scene) return;

    const wantAxis = scanPreparingSixAxisVisible;

    const removeSixAxis = () => {
      const h = scanPreparingSixAxisRef.current;
      if (!h) return;
      scene.remove(h);
      disposeObjectMaterialsAndGeometry(h);
      scanPreparingSixAxisRef.current = null;
    };

    if (!wantAxis) {
      removeSixAxis();
      return removeSixAxis;
    }

    removeSixAxis();
    const mesh = sceneRefs.shadedMesh;
    const box = new THREE.Box3();
    if (mesh) box.setFromObject(mesh);
    const size = new THREE.Vector3();
    box.getSize(size);
    const axisSize =
      Math.max(30, Math.max(size.x, size.y, size.z, 1) * 1.2) * 100;
    const helper = new SixAxisHelper(axisSize);
    helper.renderOrder = 999;
    scene.add(helper);
    scanPreparingSixAxisRef.current = helper;

    return removeSixAxis;
  }, [scanPreparingSixAxisVisible, sceneRefs?.scene, sceneRefs?.shadedMesh]);

  useEffect(() => {
    const scene = sceneRefs?.scene;
    if (!scene) return;

    const removeMetricScale = () => {
      useEditorStore.getState().setScaleHoverReadout(null);
      pointerScreenRef.current = { active: false, cx: 0, cy: 0 };
      const hm = metricScaleHoverMarkerRef.current;
      if (hm && scene) {
        scene.remove(hm);
        hm.geometry?.dispose();
        hm.material?.dispose();
        metricScaleHoverMarkerRef.current = null;
      }
      const api = metricScaleOverlayApiRef.current;
      if (api) {
        api.clearLabelHighlight?.();
        api.group.removeFromParent();
        api.dispose();
        metricScaleOverlayApiRef.current = null;
      }
    };

    if (!scaleOverlayVisible) {
      removeMetricScale();
      return removeMetricScale;
    }

    removeMetricScale();

    const corner = new THREE.Vector3(0, 0, 0);
    const mesh = sceneRefs?.shadedMesh;
    if (mesh) {
      const box = new THREE.Box3().setFromObject(mesh);
      if (!box.isEmpty()) {
        corner.set(box.min.x, box.min.y, box.min.z);
      }
    }

    const overlay = createMetricLScaleOverlay({ position: corner });
    overlay.group.renderOrder = 999;
    scene.add(overlay.group);
    metricScaleOverlayApiRef.current = overlay;

    const markerRadius = 0.28;
    const markerGeom = new THREE.SphereGeometry(markerRadius, 20, 20);
    const markerMat = new THREE.MeshBasicMaterial({
      color: 0xdc2626,
      depthTest: true,
      depthWrite: false,
      transparent: true,
      opacity: 0.98,
    });
    markerMat.toneMapped = false;
    const hoverMarker = new THREE.Mesh(markerGeom, markerMat);
    hoverMarker.name = "MetricScaleHoverMarker";
    hoverMarker.visible = false;
    hoverMarker.renderOrder = 1002;
    scene.add(hoverMarker);
    metricScaleHoverMarkerRef.current = hoverMarker;

    return removeMetricScale;
  }, [scaleOverlayVisible, sceneRefs?.scene, sceneRefs?.shadedMesh]);

  const handleAddSecondaryView = () => {
    const orderedViewIds = ['front', 'back', 'top', 'bottom', 'left', 'right'];
    const currentToggles = useEditorStore.getState().viewToggles || {};
    const nextViewId = orderedViewIds.find((id) => !currentToggles[id]);
    if (!nextViewId) return;
    setViewToggle(nextViewId, true);
  };
  const handleCloseSecondaryView = (viewId) => {
    setViewToggle(viewId, false);
  };
  const handleSwitchSecondaryView = (currentViewId, nextViewId) => {
    if (nextViewId === currentViewId) return;
    const store = useEditorStore.getState();
    const toggles = store.viewToggles || {};
    if (toggles[nextViewId]) {
      store.setViewToggle(currentViewId, false);
      return;
    }
    store.setViewToggle(currentViewId, false);
    store.setViewToggle(nextViewId, true);
  };

  useEffect(() => {
    if (didInitDefaultViewsRef.current) return;
    if (isFinalPreviewStep || isFinalizationStep) return;
    const currentToggles = useEditorStore.getState().viewToggles || {};
    const activeCount = Object.values(currentToggles).filter(Boolean).length;
    if (activeCount === 0) {
      setViewToggle('front', true);
      setViewToggle('left', true);
    }
    didInitDefaultViewsRef.current = true;
  }, [isFinalPreviewStep, isFinalizationStep, setViewToggle]);

  const modeButtons = useMemo(() => {
    const base = [
      {
        key: "ghost",
        label: "Ghost",
        icon: ghostModeIcon,
        active: ghostModeEnabled,
        onClick: toggleGhostMode,
      },
      {
        key: "color",
        label: "Color",
        icon: colorModeIcon,
        active: colorModeEnabled,
        onClick: toggleColorMode,
      },
      {
        key: "compare",
        label: "Compare",
        icon: compareModeIcon,
        active: showUnmodifiedMesh,
        onClick: toggleUnmodifiedMesh,
      },
    ];
    return [
      ...base,
      {
        key: "six-axis",
        label: "6-axis",
        textMark: "XYZ",
        active: scanPreparingSixAxisVisible,
        onClick: toggleScanPreparingSixAxis,
      },
      {
        key: "scale",
        label: "Scale",
        icon: scaleOverlayIcon,
        active: scaleOverlayVisible,
        onClick: toggleScaleOverlay,
      },
    ];
  }, [
    ghostModeEnabled,
    colorModeEnabled,
    toggleGhostMode,
    toggleColorMode,
    showUnmodifiedMesh,
    toggleUnmodifiedMesh,
    scanPreparingSixAxisVisible,
    toggleScanPreparingSixAxis,
    scaleOverlayVisible,
    toggleScaleOverlay,
  ]);

  return (
    <div ref={mountRef} className="viewer-no-select relative w-full h-full">
      <div
        ref={mainViewportRef}
        className="relative h-full min-w-0 overflow-hidden rounded-lg border border-black/10"
        style={{
          marginRight: shouldShowSecondaryViews ? 'calc(33.3333% + 8px)' : '0px',
          transition: 'margin-right 300ms ease-out',
        }}
        onPointerMove={(e) => {
          if (!useEditorStore.getState().scaleOverlayVisible) {
            pointerScreenRef.current = { active: false, cx: 0, cy: 0 };
            return;
          }
          pointerScreenRef.current = { active: true, cx: e.clientX, cy: e.clientY };
        }}
        onPointerLeave={() => {
          pointerScreenRef.current = { active: false, cx: 0, cy: 0 };
          metricScaleOverlayApiRef.current?.clearLabelHighlight?.();
          useEditorStore.getState().setScaleHoverReadout(null);
          const hm = metricScaleHoverMarkerRef.current;
          if (hm) hm.visible = false;
        }}
        onPointerCancel={() => {
          pointerScreenRef.current = { active: false, cx: 0, cy: 0 };
          metricScaleOverlayApiRef.current?.clearLabelHighlight?.();
          useEditorStore.getState().setScaleHoverReadout(null);
          const hm = metricScaleHoverMarkerRef.current;
          if (hm) hm.visible = false;
        }}
      >
        {children}
        <ScaleHoverReadout />
        <ViewControlsGroup>
          <AddViewButton
            visible={canAddSecondaryView}
            onClick={handleAddSecondaryView}
          />
          <InfoPanelToggleButton
            visible={!isFinalPreviewStep && !isFinalizationStep}
            active={rightPanelVisible}
            onClick={() => setRightPanelVisible(!rightPanelVisible)}
          />
          <MeasurementToolButtons visible={!isFinalPreviewStep && !isFinalizationStep} />
          <StepSceneControls visible={!isFinalPreviewStep && !isFinalizationStep} />
        </ViewControlsGroup>
        <ModeTogglePanel buttons={modeButtons} />
        {/* Bottom-center busy state: scoped to main viewport only (not secondary mini previews) */}
        <WorkerLoadingIndicator />
      </div>
      <div
        ref={secondaryViewsRef}
        className="absolute bottom-0 right-0 top-0 z-20 h-full overflow-hidden transition-all duration-300 ease-out"
        style={{
          width: shouldShowSecondaryViews ? '33.3333%' : '0px',
          opacity: shouldShowSecondaryViews ? 1 : 0,
          transform: shouldShowSecondaryViews ? 'translateX(0)' : 'translateX(14px)',
          pointerEvents: shouldShowSecondaryViews ? 'auto' : 'none',
        }}
      >
        <div
          id="view-grid-panel"
          className="grid h-full w-full grid-cols-1 gap-2"
          style={{ gridTemplateRows: visibleSecondaryViewIds.length <= 1 ? '1fr' : '1fr 1fr' }}
        >
          {ANATOMICAL_VIEWS.map((cfg) => {
            const isVisible = !!viewToggles?.[cfg.id];
            return (
              <div
                key={cfg.id}
                data-view-id={cfg.id}
                ref={(el) => {
                  if (el) secondaryCellRefs.current[cfg.id] = el;
                }}
                className="relative w-full min-h-0 overflow-hidden rounded-lg border border-black/10"
                style={{ display: isVisible ? 'block' : 'none' }}
              >

                {/* Canvas Wrap */}
                <div
                  ref={(el) => {
                    if (el) secondaryCanvasWrapRefs.current[cfg.id] = el;
                  }}
                  className="absolute inset-0"
                />
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.2)_35%,transparent_70%)]" />
                <div className="pointer-events-none absolute left-2.5 top-2.5 z-[2] text-[11px] font-bold text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.8)]">
                  {cfg.name}
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  aria-label={`Close ${cfg.name}`}
                  className="absolute right-2.5 top-2.5 z-[3] flex h-6 w-6 items-center justify-center rounded bg-red-100 p-0 text-lg leading-none text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseSecondaryView(cfg.id);
                  }}
                >
                  <X size={12} strokeWidth={2} aria-hidden />
                </button>

                {/* View Switcher */}
                <div className="absolute bottom-2.5 right-2.5 z-[3] flex border border-black/10">
                  {ANATOMICAL_VIEWS.map((viewCfg, index) => {
                    const selected = viewCfg.id === cfg.id;
                    const activeElsewhere = !!viewToggles?.[viewCfg.id] && !selected;
                    return (
                      <button
                        key={viewCfg.id}
                        type="button"
                        aria-label={`Switch ${cfg.name} to ${viewCfg.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSwitchSecondaryView(cfg.id, viewCfg.id);
                        }}
                        className={cn("h-6 w-6 cursor-pointer px-1.5 text-[10px] font-bold leading-none text-black", selected ? "bg-odp-purple-light text-white" : " bg-[#fafafa]/50 text-text-primary hover:bg-white", 
                          index !== ANATOMICAL_VIEWS.length - 1 ? "border-r border-black/10" : "",
                          index === 0 ? "rounded-l-sm" : "",
                          index === ANATOMICAL_VIEWS.length - 1 ? "rounded-r-sm" : "",
                        )}
                      >
                        {VIEW_SHORT_LABELS[viewCfg.id] || viewCfg.name[0]}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <ToolHandler />
    </div>
  );
};

const AddViewButton = React.memo(({ visible, onClick }) => {
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Add secondary view"
      className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-md border border-black/15 bg-[#fafafa]/50 p-2 text-xs font-semibold text-[var(--text-primary)] transition-colors hover:bg-white"
    >
      <Plus size={20} strokeWidth={2} aria-hidden />
    </button>
  );
});

const InfoPanelToggleButton = React.memo(({ visible, active, onClick }) => {
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? "Hide info panel" : "Show info panel"}
      className={`pointer-events-auto flex h-10 w-10 items-center justify-center rounded-md border text-base leading-none transition-colors ${active
        ? "border-[rgba(88,60,163,0.35)] bg-[var(--odp-purple-light)] text-white"
        : "border-black/15 bg-[#fafafa]/50 text-[var(--text-primary)] hover:bg-white"
        }`}
      aria-pressed={active}
    >
      <Settings size={20} strokeWidth={2} aria-hidden />
    </button>
  );
});

const ViewControlsGroup = React.memo(({ children }) => {
  return (
    <div className="pointer-events-none absolute right-2.5 top-[140px] z-40 flex flex-col gap-1">
      {children}
    </div>
  );
});

const ModeTogglePanel = React.memo(({ buttons }) => {
  return (
    <div className="pointer-events-none absolute top-2.5 right-[140px] z-40">
      <div className="pointer-events-auto flex items-center gap-1">
        {buttons.map(({ key, label, icon, textMark, active, onClick }) => (
          <ModeToggleButton
            key={key}
            label={label}
            icon={icon}
            textMark={textMark}
            active={active}
            onClick={onClick}
          />
        ))}
      </div>
    </div>
  );
});

const ModeToggleButton = ({ icon, label, textMark, active, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={`${label} mode ${active ? "enabled" : "disabled"}`}
      className={`group relative flex min-h-10 min-w-10 cursor-pointer items-center justify-center rounded-md border px-2 py-2 touch-manipulation transition-colors ${active ? "bg-[var(--odp-purple-light)] border-[rgba(88,60,163,0.35)] text-white shadow-[0_0_0_1px_rgba(88,60,163,0.12)]" : "bg-[#fafafa]/50 border-black/10 text-[var(--text-primary)] hover:border-black/[0.14] hover:bg-[#f4f4f5]"}`}
    >
      <div
        className={`flex h-5 w-5 items-center justify-center rounded transition-all duration-200 ${active
          ? "border-white/20 bg-white/10"
          : "border-transparent bg-transparent"
          }`}
      >
        {textMark ? (
          <span
            className={`select-none text-[0.62rem] font-bold leading-none tracking-tight transition-opacity duration-200 ${active ? "text-white opacity-100" : "text-[var(--text-primary)] opacity-80 group-hover:opacity-100"
              }`}
            aria-hidden
          >
            {textMark}
          </span>
        ) : (
          <img
            src={icon}
            alt=""
            className={`h-5 w-5 select-none transition-transform duration-200 ${active ? "opacity-100" : "opacity-70 group-hover:opacity-100"
              }`}
          />
        )}
      </div>
      <span className="sr-only">{label} mode toggle</span>
    </button>
  );
};
